import React, { useEffect, useState, useContext } from "react";
import { Link as RouterLink } from "react-router-dom";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Link from "@material-ui/core/Link";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import { versionSystem } from "../../../package.json";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import logo from "../../assets/logo.png";
import {
  LanguageOutlined,
  Visibility,
  VisibilityOff,
  WhatsApp,
  Gavel,
  Security,
  VideoCall,
} from "@material-ui/icons";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
} from "@material-ui/core";
import LanguageControl from "../../components/LanguageControl";

const B = "#059669";
const BD = "#047857";
const BL = "#34D399";
const SIDEBAR = "#0F172A";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100vw",
    height: "100vh",
    display: "flex",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
    },
  },
  // Left brand panel
  brandPanel: {
    width: 420,
    minWidth: 420,
    height: "100%",
    background: `linear-gradient(160deg, ${SIDEBAR} 0%, #1E293B 50%, #0F2A1F 100%)`,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    padding: theme.spacing(6),
    position: "relative",
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
    // Animated floating orbs
    "&::before": {
      content: '""',
      position: "absolute",
      top: -80,
      right: -80,
      width: 280,
      height: 280,
      borderRadius: "50%",
      background: "radial-gradient(circle, rgba(5,150,105,0.2) 0%, rgba(5,150,105,0) 70%)",
      animation: "float-orb 12s ease-in-out infinite",
    },
    "&::after": {
      content: '""',
      position: "absolute",
      bottom: -60,
      left: -60,
      width: 220,
      height: 220,
      borderRadius: "50%",
      background: "radial-gradient(circle, rgba(52,211,153,0.15) 0%, rgba(52,211,153,0) 70%)",
      animation: "float-orb 15s ease-in-out infinite reverse",
    },
  },
  brandOrb3: {
    position: "absolute",
    top: "50%",
    right: "20%",
    width: 150,
    height: 150,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(124,58,237,0.1) 0%, rgba(124,58,237,0) 70%)",
    animation: "float-orb 10s ease-in-out infinite 2s",
    pointerEvents: "none",
  },
  brandGrid: {
    position: "absolute",
    inset: 0,
    backgroundImage: `linear-gradient(rgba(255,255,255,0.02) 1px, transparent 1px),
                       linear-gradient(90deg, rgba(255,255,255,0.02) 1px, transparent 1px)`,
    backgroundSize: "40px 40px",
    pointerEvents: "none",
  },
  brandLogo: {
    width: 220,
    marginBottom: theme.spacing(4),
    position: "relative",
    zIndex: 1,
    filter: "drop-shadow(0 4px 12px rgba(0,0,0,0.3))",
  },
  brandTitle: {
    color: "#fff",
    fontSize: 28,
    fontWeight: 700,
    lineHeight: 1.3,
    marginBottom: theme.spacing(1.5),
    position: "relative",
    zIndex: 1,
  },
  brandSubtitle: {
    color: "rgba(255,255,255,0.6)",
    fontSize: 15,
    lineHeight: 1.6,
    marginBottom: theme.spacing(4),
    position: "relative",
    zIndex: 1,
  },
  brandFeature: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    color: "rgba(255,255,255,0.85)",
    fontSize: 14,
    fontWeight: 500,
    position: "relative",
    zIndex: 1,
    transition: "transform 0.2s ease, color 0.2s ease",
    "&:hover": {
      transform: "translateX(4px)",
      color: "#fff",
    },
  },
  brandFeatureIcon: {
    color: BL,
    fontSize: 22,
  },
  brandFooter: {
    position: "absolute",
    bottom: theme.spacing(3),
    left: theme.spacing(6),
    color: "rgba(255,255,255,0.35)",
    fontSize: 12,
    zIndex: 1,
  },

  // Right form panel
  formPanel: {
    flex: 1,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#f8fafb",
    padding: theme.spacing(4),
    overflowY: "auto",
    position: "relative",
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
      alignItems: "flex-start",
      paddingTop: theme.spacing(4),
    },
  },
  formBgDots: {
    position: "absolute",
    inset: 0,
    backgroundImage: "radial-gradient(circle, rgba(5,150,105,0.06) 1px, transparent 1px)",
    backgroundSize: "24px 24px",
    pointerEvents: "none",
  },

  // Card with glassmorphism
  card: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: "rgba(255,255,255,0.92)",
    backdropFilter: "blur(20px)",
    WebkitBackdropFilter: "blur(20px)",
    borderRadius: 20,
    boxShadow: "0 8px 40px rgba(15,23,42,0.08), 0 1px 3px rgba(15,23,42,0.06)",
    border: "1px solid rgba(255,255,255,0.6)",
    padding: theme.spacing(4, 4, 3),
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    position: "relative",
    zIndex: 1,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(3, 2.5, 2.5),
      borderRadius: 16,
    },
  },
  // Mount animation
  cardEnter: {
    animation: "$cardMount 0.6s cubic-bezier(0.4, 0, 0.2, 1) both",
  },
  "@keyframes cardMount": {
    "0%": {
      opacity: 0,
      transform: "translateY(24px) scale(0.97)",
    },
    "100%": {
      opacity: 1,
      transform: "translateY(0) scale(1)",
    },
  },
  logoWrapMobile: {
    display: "none",
    [theme.breakpoints.down("sm")]: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      marginBottom: theme.spacing(1),
    },
  },
  logoMobile: {
    maxWidth: 200,
    width: "100%",
    height: "auto",
  },
  formTitle: {
    fontSize: 24,
    fontWeight: 700,
    color: SIDEBAR,
    marginBottom: theme.spacing(0.5),
    alignSelf: "flex-start",
  },
  formSubtitle: {
    fontSize: 14,
    color: "rgba(0,0,0,0.5)",
    marginBottom: theme.spacing(2.5),
    alignSelf: "flex-start",
  },
  form: {
    width: "100%",
  },
  input: {
    marginBottom: theme.spacing(1),
    "& .MuiOutlinedInput-root": {
      borderRadius: 12,
      backgroundColor: "#fff",
      transition: "all 0.2s ease",
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: "rgba(5,150,105,0.4)",
      },
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: B,
      boxShadow: "0 0 0 3px rgba(5,150,105,0.08)",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: B,
    },
  },
  submit: {
    margin: theme.spacing(2, 0, 1.5),
    borderRadius: 12,
    fontWeight: 700,
    padding: "13px 0",
    fontSize: 15,
    background: `linear-gradient(135deg, ${B} 0%, ${BD} 100%)`,
    color: "#fff",
    boxShadow: "0 4px 14px rgba(5,150,105,0.3)",
    textTransform: "none",
    transition: "all 0.25s ease",
    "&:hover": {
      background: `linear-gradient(135deg, ${BL} 0%, ${B} 100%)`,
      boxShadow: "0 6px 24px rgba(5,150,105,0.4)",
      transform: "translateY(-1px)",
    },
    "&:active": {
      transform: "translateY(0) scale(0.98)",
    },
  },
  divider: {
    display: "flex",
    alignItems: "center",
    margin: theme.spacing(2, 0),
    width: "100%",
    "& hr": { flex: 1, border: "none", borderTop: "1px solid rgba(0,0,0,0.08)" },
    "& span": { padding: theme.spacing(0, 2), color: "rgba(0,0,0,0.35)", fontSize: 13 },
  },
  ssoButton: {
    borderRadius: 12,
    fontWeight: 600,
    padding: "11px 0",
    textTransform: "none",
    background: "#fff",
    color: SIDEBAR,
    border: "1.5px solid rgba(0,0,0,0.12)",
    boxShadow: "none",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: B,
      color: B,
      boxShadow: "0 2px 12px rgba(5,150,105,0.12)",
      transform: "translateY(-1px)",
    },
  },
  appsBox: {
    marginTop: theme.spacing(2.5),
    borderRadius: 14,
    background: "rgba(5,150,105,0.03)",
    border: "1px solid rgba(5,150,105,0.08)",
    padding: theme.spacing(2),
    display: "flex",
    gap: theme.spacing(1),
    justifyContent: "center",
  },
  appCard: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 4,
    padding: theme.spacing(1.5, 1),
    borderRadius: 10,
    textDecoration: "none",
    color: "rgba(0,0,0,0.65)",
    fontSize: 11,
    fontWeight: 600,
    transition: "all 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(5,150,105,0.06)",
      color: B,
      transform: "translateY(-2px)",
    },
  },
  appCardIcon: {
    width: 36,
    height: 36,
    borderRadius: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    "& svg": { fontSize: 20 },
  },
  linksWrap: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    marginTop: theme.spacing(1.5),
    width: "100%",
  },
  linkRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: "rgba(0,0,0,0.55)",
    textDecoration: "none",
    fontSize: 13,
    fontWeight: 500,
    transition: "color 0.2s ease",
    "&:hover": { color: B },
  },
  rememberRow: {
    marginTop: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
  },
  rememberLabel: {
    "& .MuiFormControlLabel-label": {
      fontSize: 13,
      color: "rgba(0,0,0,0.6)",
    },
  },
  languageControl: {
    position: "absolute",
    top: 12,
    right: 12,
    zIndex: 10,
  },
  copyright: {
    marginTop: theme.spacing(2.5),
    fontSize: 11,
    color: "rgba(0,0,0,0.3)",
    textAlign: "center",
    letterSpacing: 0.3,
  },
}));

const Login = () => {
  const classes = useStyles();
  const [user, setUser] = useState({ email: "", password: "" });
  const [remember, setRemember] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [anchorElLanguage, setAnchorElLanguage] = useState(null);
  const [menuLanguageOpen, setMenuLanguageOpen] = useState(false);
  const [mounted, setMounted] = useState(false);
  const { handleLogin } = useContext(AuthContext);

  useEffect(() => {
    const t = setTimeout(() => setMounted(true), 50);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    try {
      const savedEmail = localStorage.getItem("login_email") || "";
      const savedRemember = localStorage.getItem("login_remember") !== "false";
      setRemember(savedRemember);
      if (savedRemember && savedEmail) setUser((prev) => ({ ...prev, email: savedEmail }));
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("login_remember", remember ? "true" : "false");
      if (!remember) localStorage.removeItem("login_email");
    } catch (err) { /* ignore */ }
  }, [remember]);

  const handleChangeInput = (e) => {
    const next = { ...user, [e.target.name]: e.target.value };
    setUser(next);
    if (remember && e.target.name === "email") {
      try { localStorage.setItem("login_email", e.target.value); } catch (err) { /* ignore */ }
    }
  };

  const handlSubmit = (e) => {
    e.preventDefault();
    handleLogin(user);
  };

  return (
    <div className={classes.root}>
      <div className={classes.brandPanel}>
        <div className={classes.brandGrid} />
        <div className={classes.brandOrb3} />
        <img src={logo} className={classes.brandLogo} alt="Facile Macile" />
        <div className={classes.brandTitle}>
          Suporte &amp; CRM<br />inteligente para você
        </div>
        <div className={classes.brandSubtitle}>
          Gerencie tickets, automatize fluxos e conecte sua equipe &mdash; tudo em um só lugar.
        </div>
        <div className={classes.brandFeature}>
          <WhatsApp className={classes.brandFeatureIcon} />
          Atendimento multi-canal WhatsApp
        </div>
        <div className={classes.brandFeature}>
          <Gavel className={classes.brandFeatureIcon} />
          CRM e gestão de casos completa
        </div>
        <div className={classes.brandFeature}>
          <Security className={classes.brandFeatureIcon} />
          SSO e autenticação centralizada
        </div>
        <div className={classes.brandFeature}>
          <VideoCall className={classes.brandFeatureIcon} />
          Videoconferências integradas
        </div>
        <div className={classes.brandFooter}>
          Facile Macile &middot; Suporte &amp; CRM &middot; v{versionSystem}
        </div>
      </div>

      <div className={classes.formPanel}>
        <div className={classes.formBgDots} />
        <div className={classes.languageControl}>
          <IconButton
            edge="start"
            onClick={(e) => { setAnchorElLanguage(e.currentTarget); setMenuLanguageOpen(true); }}
            style={{ color: "rgba(0,0,0,0.45)" }}
          >
            <LanguageOutlined />
          </IconButton>
          <Menu
            anchorEl={anchorElLanguage}
            getContentAnchorEl={null}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            open={menuLanguageOpen}
            onClose={() => { setAnchorElLanguage(null); setMenuLanguageOpen(false); }}
          >
            <MenuItem><LanguageControl /></MenuItem>
          </Menu>
        </div>

        <div className={`${classes.card} ${mounted ? classes.cardEnter : ""}`}>
          <div className={classes.logoWrapMobile}>
            <img className={classes.logoMobile} src={logo} alt="Facile Macile" />
          </div>

          <Typography className={classes.formTitle}>Bem-vindo de volta</Typography>
          <Typography className={classes.formSubtitle}>Entre com suas credenciais para continuar</Typography>

          <form className={classes.form} noValidate onSubmit={handlSubmit}>
            <TextField
              variant="outlined" margin="normal" required fullWidth
              id="email" label={i18n.t("login.form.email")} name="email"
              value={user.email} onChange={handleChangeInput}
              autoComplete="email" autoFocus className={classes.input}
            />
            <TextField
              variant="outlined" margin="normal" required fullWidth
              name="password" label={i18n.t("login.form.password")}
              type={showPassword ? "text" : "password"} id="password"
              value={user.password} onChange={handleChangeInput}
              autoComplete="current-password" className={classes.input}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword((v) => !v)} edge="end" size="small">
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <div className={classes.rememberRow}>
              <FormControlLabel
                className={classes.rememberLabel}
                control={<Checkbox checked={remember} onChange={(e) => setRemember(e.target.checked)} color="primary" size="small" />}
                label="Lembrar email"
              />
              <Link component={RouterLink} to="/forgetpsw" className={classes.linkRow} style={{ fontSize: 12 }}>
                Esqueceu a senha?
              </Link>
            </div>

            <Button type="submit" fullWidth variant="contained" className={classes.submit}>
              {i18n.t("login.buttons.submit")}
            </Button>

            <div className={classes.divider}><hr /><span>ou continue com</span><hr /></div>

            <Button
              fullWidth variant="outlined" className={classes.ssoButton}
              onClick={() => {
                window.location.href = "https://auth.damineweb.work/authorize?client_id=atendechat&redirect_uri=https%3A%2F%2Fsuporte.damineweb.work%2Fsso%2Fcallback&response_type=code&scope=openid+profile+email";
              }}
            >
              <Security style={{ marginRight: 8, fontSize: 18 }} />
              Entrar com SSO Centralizado
            </Button>

            <div className={classes.appsBox}>
              <a
                className={classes.appCard}
                href="https://bbb.damineweb.work"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className={classes.appCardIcon} style={{ backgroundColor: "rgba(124,58,237,0.1)", color: "#7c3aed" }}>
                  <VideoCall />
                </div>
                VMeet
              </a>
              <a
                className={classes.appCard}
                href="https://advogado.damineweb.work"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className={classes.appCardIcon} style={{ backgroundColor: "rgba(5,150,105,0.1)", color: B }}>
                  <Gavel />
                </div>
                CRM
              </a>
              <a
                className={classes.appCard}
                href="https://auth.damineweb.work/dashboard"
                target="_blank"
                rel="noopener noreferrer"
              >
                <div className={classes.appCardIcon} style={{ backgroundColor: "rgba(59,130,246,0.1)", color: "#3b82f6" }}>
                  <Security />
                </div>
                SSO
              </a>
            </div>

            <div className={classes.linksWrap}>
              <Link component={RouterLink} to="/signup" className={classes.linkRow}>
                Não tem uma conta? <strong style={{ marginLeft: 4 }}>Cadastre-se</strong>
              </Link>
            </div>
          </form>

          <div className={classes.copyright}>
            Facile Macile &middot; Suporte &amp; CRM &middot; v{versionSystem} &middot; {new Date().getFullYear()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
